/* eslint-disable no-underscore-dangle */

import semverGte from 'semver/functions/gte';
import semverLt from 'semver/functions/lt';

import { IS_INDIAN_EXCHANGE, isRNWebview } from './constants';

enum BIOMETRICS_AUTH_INTRODUCED_VERSIONS {
  ANDROID = '1.7.5',
  IOS = '1.6.6',
}

/**
 @description Use this variable instead of hardcoding '10000.0.0' for a feature thats not planned to be included anytime soon.
*/
const FEATURE_UNAVAILABLE_VERSION = '10000.0.0';

const OAUTH_INTRODUCED_VERSIONS = {
  ANDROID: IS_INDIAN_EXCHANGE ? '1.1.0' : '1.9.0',
  IOS: IS_INDIAN_EXCHANGE ? FEATURE_UNAVAILABLE_VERSION : '1.9.0',
} as const;

const HYPERVERGE_SDK_INTRODUCED_VERSIONS = {
  ANDROID: IS_INDIAN_EXCHANGE ? '1.2.0' : '1.9.3',
  IOS: IS_INDIAN_EXCHANGE ? '1.2.13' : FEATURE_UNAVAILABLE_VERSION,
} as const;

const HYPERVERGE_CUSTOM_INPUT_INTRODUCED_VERSIONS = {
  android: IS_INDIAN_EXCHANGE ? '1.2.8' : FEATURE_UNAVAILABLE_VERSION,
  ios: IS_INDIAN_EXCHANGE ? '1.2.13' : FEATURE_UNAVAILABLE_VERSION,
} as const;

const SHUFTIPRO_SDK_INTRODUCED_VERSIONS = {
  ANDROID: '1.9.4',
} as const;

const SOCIAL_SHARING_CARD_INTRODUCED_VERSIONS = {
  android: IS_INDIAN_EXCHANGE ? '1.2.0' : '1.9.3',
  ios: IS_INDIAN_EXCHANGE ? '1.2.0' : '1.8.2',
} as const;

const TRADE_SUCCESS_CARDS_INTRODUCED_VERSIONS = {
  android: IS_INDIAN_EXCHANGE ? '1.2.13' : FEATURE_UNAVAILABLE_VERSION,
  ios: IS_INDIAN_EXCHANGE ? '1.2.13' : FEATURE_UNAVAILABLE_VERSION,
} as const;

const IDFY_WEBVIEW_INTRODUCED_VERSIONS = {
  android: IS_INDIAN_EXCHANGE ? '1.2.13' : FEATURE_UNAVAILABLE_VERSION,
  ios: IS_INDIAN_EXCHANGE ? '1.2.13' : FEATURE_UNAVAILABLE_VERSION,
} as const;

const DOWNLOAD_INVOICE_INTRODUCED_VERSION = {
  android: '1.9.4',
  ios: '1.8.3',
};

const IN_APP_REVIEW_INTRODUCED_VERSION = {
  android: IS_INDIAN_EXCHANGE ? '1.2.0' : '1.10.0',
  ios: IS_INDIAN_EXCHANGE ? '1.2.1' : FEATURE_UNAVAILABLE_VERSION,
};

const SCAN_TO_LOGIN_INTRODUCED_VERSIONS = {
  android: IS_INDIAN_EXCHANGE ? '1.2.13' : '1.7.0',
  ios: IS_INDIAN_EXCHANGE ? '1.2.13' : '1.6.1',
} as const;

const RENEW_KYC_SELFIE_VERIFICATION_INTRODUCED_VERSION = '2.6.1';

const HYBRID_APP_INTRODUCED_VERSION = '2.0.0';

const AUTO_MOBILE_OTP_VERIFICATION = {
  android: IS_INDIAN_EXCHANGE ? '2.5.0' : FEATURE_UNAVAILABLE_VERSION,
  ios: FEATURE_UNAVAILABLE_VERSION,
} as const;

/**
 * The platform that the mobile app is running on.
 * @enum {string} 'ios' | 'android' | 'windows' | 'macos' | 'web'
 */
const RN_APP_PLATFORM = window._RNAppPlatform;

/**
 * The version of the mobile app that is currently running.
 */
// const RN_APP_VERSION = window._RNAppVersion;
/**
 * Used for global mobile app!
 */
const RN_APP_VERSION = RN_APP_PLATFORM === 'ios' ? window._RNAppVersion : '1.7.5';

/**
 * Used for India mobile app!
 */
const NATIVE_APP_VERSION = window._RNAppVersion;

const RN_APP_VERSION_FOR_OAUTH = NATIVE_APP_VERSION;

// #region Biometrics Auth feature-flag "FEATURE_FLAG_BIOMETRICS_AUTH_ENABLED"
const DEFAULT_BIOMETRICS_AUTH_ENABLED_FLAG = false;

// Disable Google oauth by default
const DEFAULT_OAUTH_ENABLED_FLAG = false;

let isBiometricsAuthEnabled = DEFAULT_BIOMETRICS_AUTH_ENABLED_FLAG;
let isOAuthEnabled = DEFAULT_OAUTH_ENABLED_FLAG;

const biometricsAuthIntroducedVersionByPlatform =
  BIOMETRICS_AUTH_INTRODUCED_VERSIONS[RN_APP_PLATFORM?.toUpperCase()];

const oAuthIntroducedVersionByPlatform =
  OAUTH_INTRODUCED_VERSIONS[RN_APP_PLATFORM?.toUpperCase()];

if (biometricsAuthIntroducedVersionByPlatform) {
  isBiometricsAuthEnabled = semverGte(
    RN_APP_VERSION,
    biometricsAuthIntroducedVersionByPlatform
  );
}

if (oAuthIntroducedVersionByPlatform) {
  isOAuthEnabled = semverGte(RN_APP_VERSION_FOR_OAUTH, oAuthIntroducedVersionByPlatform);
}

/**
 * This feature-flag is used to enable/disable the biometrics auth feature.
 * It is disabled by default, but is enabled for version above the minimum
 * version that supports biometrics auth.
 *
 * @see BIOMETRICS_AUTH_INTRODUCED_VERSIONS for the minimum version that supports biometrics auth.
 */
const FEATURE_FLAG_BIOMETRICS_AUTH_ENABLED = isBiometricsAuthEnabled;

// ENABLE or DISABLE google oauth feature in this flag
const FEATURE_FLAG_OAUTH_ENABLED = isOAuthEnabled;
// #endregion

/**
 * @description Currently Hyperverge Mobile SDK is only working on Android platform.
 * Also it is only supported by minimum versions mentioned in variable `HYPERVERGE_SDK_INTRODUCED_VERSIONS`
 */
const FEATURE_FLAG_HYPERVERGE_SDK_SUPPORTED = NATIVE_APP_VERSION
  ? semverGte(
      NATIVE_APP_VERSION,
      HYPERVERGE_SDK_INTRODUCED_VERSIONS[RN_APP_PLATFORM.toUpperCase()]
    )
  : false;

/**
 * @description Currently ShuftiPro Mobile SDK is only working on Android platform.
 * Also it is only supported by minimum versions mentioned in variable `SHUFTIPRO_SDK_INTRODUCED_VERSIONS`
 */
const FEATURE_FLAG_SHUFTIPRO_SDK_SUPPORTED =
  RN_APP_PLATFORM === 'android' && NATIVE_APP_VERSION
    ? semverGte(NATIVE_APP_VERSION, SHUFTIPRO_SDK_INTRODUCED_VERSIONS.ANDROID)
    : false;

const FEATURE_FLAG_SOCIAL_SHARING_CARD_SUPPORTED = NATIVE_APP_VERSION
  ? semverGte(
      NATIVE_APP_VERSION,
      SOCIAL_SHARING_CARD_INTRODUCED_VERSIONS[RN_APP_PLATFORM]
    )
  : false;

const FEATURE_FLAG_TRADE_SUCCESS_CARD_SUPPORTED = NATIVE_APP_VERSION
  ? semverGte(
      NATIVE_APP_VERSION,
      TRADE_SUCCESS_CARDS_INTRODUCED_VERSIONS[RN_APP_PLATFORM]
    )
  : false;

/**
 *  @description  flag used for checking download invoice feature is available or not,
 *  depending on `DOWNLOAD_INVOICE_INTRODUCED_VERSION`
 */
const FEATURE_FLAG_DOWNLOAD_INVOICE_ENABLED = NATIVE_APP_VERSION
  ? semverGte(NATIVE_APP_VERSION, DOWNLOAD_INVOICE_INTRODUCED_VERSION[RN_APP_PLATFORM])
  : false;

const FEATURE_FLAG_IN_APP_REVIEW_SUPPORTED = NATIVE_APP_VERSION
  ? semverGte(NATIVE_APP_VERSION, IN_APP_REVIEW_INTRODUCED_VERSION[RN_APP_PLATFORM])
  : false;

const FEATURE_FLAG_HYPERVERGE_CUSTOM_INPUT_SUPPORTED = NATIVE_APP_VERSION
  ? semverGte(
      NATIVE_APP_VERSION,
      HYPERVERGE_CUSTOM_INPUT_INTRODUCED_VERSIONS[RN_APP_PLATFORM]
    )
  : false;

const FEATURE_FLAG_IDFY_WEBVIEW_SUPPORTED = NATIVE_APP_VERSION
  ? semverGte(NATIVE_APP_VERSION, IDFY_WEBVIEW_INTRODUCED_VERSIONS[RN_APP_PLATFORM])
  : false;

/**
 * Flag to check if SCAN_TO_LOGIN feature is supported by current version of react-native app
 */
const FEATURE_FLAG_SCAN_TO_LOGIN_SUPPORTED = NATIVE_APP_VERSION
  ? semverGte(NATIVE_APP_VERSION, SCAN_TO_LOGIN_INTRODUCED_VERSIONS[RN_APP_PLATFORM])
  : false;

/**
 *  Flag to check if the app is global or not
 * _RNAppPlatformVersionAndroid is only injected from global app
 */
const IS_GLOBAL_APP = typeof window._RNAppPlatformVersionAndroid === 'string';

const INDIA_APP_FORCE_UPDATE_VERSION = '2.7.0';

const FORCE_APP_UPDATE_POPUP_ENABLED = IS_INDIAN_EXCHANGE && IS_GLOBAL_APP;
const FORCE_APP_UPDATE_FOR_NATIVE =
  IS_INDIAN_EXCHANGE &&
  isRNWebview() &&
  NATIVE_APP_VERSION &&
  semverLt(NATIVE_APP_VERSION, INDIA_APP_FORCE_UPDATE_VERSION);

const FEATURE_FLAG_IS_NATIVE_APP_ENABLED = true;

const IS_HYBRID_APP_SUPPORTED =
  FEATURE_FLAG_IS_NATIVE_APP_ENABLED &&
  IS_INDIAN_EXCHANGE &&
  isRNWebview() &&
  semverGte(window._RNAppVersion!, HYBRID_APP_INTRODUCED_VERSION);

const IS_REKYC_SELFIE_VERIFICATION_NATIVE_SUPPORTED =
  isRNWebview() &&
  semverGte(window._RNAppVersion!, RENEW_KYC_SELFIE_VERIFICATION_INTRODUCED_VERSION);

const FEATURE_FLAG_AUTO_MOBILE_OTP_VERIFICATION = window._RNAppVersion
  ? semverGte(window._RNAppVersion, AUTO_MOBILE_OTP_VERIFICATION[RN_APP_PLATFORM])
  : false;

export {
  FEATURE_FLAG_AUTO_MOBILE_OTP_VERIFICATION,
  FEATURE_FLAG_BIOMETRICS_AUTH_ENABLED,
  FEATURE_FLAG_DOWNLOAD_INVOICE_ENABLED,
  FEATURE_FLAG_HYPERVERGE_CUSTOM_INPUT_SUPPORTED,
  FEATURE_FLAG_HYPERVERGE_SDK_SUPPORTED,
  FEATURE_FLAG_IDFY_WEBVIEW_SUPPORTED,
  FEATURE_FLAG_IN_APP_REVIEW_SUPPORTED,
  FEATURE_FLAG_OAUTH_ENABLED,
  FEATURE_FLAG_SCAN_TO_LOGIN_SUPPORTED,
  FEATURE_FLAG_SHUFTIPRO_SDK_SUPPORTED,
  FEATURE_FLAG_SOCIAL_SHARING_CARD_SUPPORTED,
  FEATURE_FLAG_TRADE_SUCCESS_CARD_SUPPORTED,
  FORCE_APP_UPDATE_POPUP_ENABLED,
  HYBRID_APP_INTRODUCED_VERSION,
  IS_GLOBAL_APP,
  IS_HYBRID_APP_SUPPORTED,
  IS_REKYC_SELFIE_VERIFICATION_NATIVE_SUPPORTED,
  RN_APP_PLATFORM,
  RN_APP_VERSION,
  RN_APP_VERSION_FOR_OAUTH,
  FORCE_APP_UPDATE_FOR_NATIVE,
  SCAN_TO_LOGIN_INTRODUCED_VERSIONS,
};
